import React, { useState, useEffect } from "react";
import Confetti from "react-confetti";
import moment from "moment";
import UserServices from "../../../Services/UserService";
import secureLocalStorage from "react-secure-storage";

// Module-level (not component state) so it survives Main remounting via its
// `key={forceUpdate}` — which happens on "permissionsUpdated"/"companyModulesUpdated"
// events during normal app bootstrap. Without this, two near-simultaneous remounts can
// both pass the "not shown yet" check before either finishes writing to localStorage,
// showing the popup more than once. Resets naturally on an actual page reload.
let hasInitiatedBirthdayCheckThisPageLoad = false;

const BirthdayAnimation = () => {
    const [showConfetti, setShowConfetti] = useState(false);
    const [birthdayUsers, setBirthdayUsers] = useState([]);
    const [windowDimensions, setWindowDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const checkBirthdays = async () => {
            if (hasInitiatedBirthdayCheckThisPageLoad) return;
            hasInitiatedBirthdayCheckThisPageLoad = true;

            const loggedUser = JSON.parse(secureLocalStorage.getItem("adminInfo"));
            if (!loggedUser) return;

            const companyId = loggedUser?.user?.userdata?.company_id;
            const userId = loggedUser?.user?.userdata?.id;

            if (!companyId || !userId) return;

            // Generate a unique daily key per user to prevent re-playing on every navigation
            const todayStr = moment().format("YYYY-MM-DD");
            const storageKey = `birthday_celebrated_${userId}_${todayStr}`;

            if (localStorage.getItem(storageKey) === "true") {
                // Animation already played for this user today
                return;
            }

            try {
                const res = await UserServices.getUserSummary(companyId);
                if (res.Good && res.data && res.data.currentMonthBirthdaysList) {
                    const todayFormatted = moment().format("Do MMMM"); // e.g. "3rd March"

                    // Find all users who have a birthday today
                    const todaysBirthdays = res.data.currentMonthBirthdaysList.filter(
                        (b) => b.dob === todayFormatted
                    );

                    if (todaysBirthdays.length > 0) {
                        setBirthdayUsers(todaysBirthdays.map(b => b.full_name));
                        setShowConfetti(true);
                        localStorage.setItem(storageKey, "true");

                        // Let the confetti run for 10 seconds then automatically dismiss
                        setTimeout(() => {
                            setShowConfetti(false);
                        }, 10000);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch birthdays for animation:", err);
            }
        };

        checkBirthdays();
    }, []);

    if (!showConfetti) return null;

    return (
        <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 99999, // Ensure it's on top of everything
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none" // Prevents background layer from blocking clicks
        }}>
            {/* Confetti Layer */}
            <Confetti
                width={windowDimensions.width}
                height={windowDimensions.height}
                recycle={false} // Only drop the initial pieces and stop generating new ones
                numberOfPieces={600}
                gravity={0.12}
                initialVelocityY={20}
                style={{ pointerEvents: "none" }}
            />

            {/* Popup Modal Layer */}
            <div
                style={{
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    borderRadius: "24px",
                    padding: "40px 50px",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(13, 223, 171, 0.1) inset",
                    textAlign: "center",
                    maxWidth: "500px",
                    width: "90%",
                    pointerEvents: "auto", // Allow clicking inside the modal
                    animation: "bounceIn 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) both",
                    position: "relative"
                }}
            >
                {/* Close Button */}
                <button
                    onClick={() => setShowConfetti(false)}
                    style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        color: "#999",
                        cursor: "pointer",
                        width: "32px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        transition: "all 0.2s ease"
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f0f0f0";
                        e.currentTarget.style.color = "#333";
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#999";
                    }}
                >
                    &times;
                </button>

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        marginBottom: "20px"
                    }}
                >
                    <span style={{ fontSize: "64px", lineHeight: 1 }}>🎉</span>
                </div>

                <h1 style={{
                    fontSize: "36px",
                    fontWeight: "800",
                    color: "#0f223a",
                    marginBottom: "15px",
                    fontFamily: "'Inter', sans-serif",
                    background: "linear-gradient(135deg, #0DDFAB 0%, #008285 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent"
                }}>
                    Happy Birthday!
                </h1>

                <p style={{
                    fontSize: "22px",
                    fontWeight: "600",
                    color: "#333",
                    marginBottom: "25px",
                }}>
                    {birthdayUsers.join(" & ")}
                </p>

                <p style={{
                    fontSize: "16px",
                    color: "#666",
                    lineHeight: "1.6",
                    marginBottom: "30px"
                }}>
                    Wishing {birthdayUsers.join(" & ")} a spectacular day filled with happiness and a wonderful year ahead! 🍰🎈
                </p>
            </div>

            {/* CSS Animation Definitions injected inline safely */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes bounceIn {
                    0% { transform: scale(0.3); opacity: 0; }
                    50% { transform: scale(1.05); opacity: 1; }
                    70% { transform: scale(0.9); }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}} />
        </div>
    );
};

export default BirthdayAnimation;
